'use client'

import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  Row,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import * as React from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import { Button } from '../button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../dropdown-menu'
import { Icon } from '../icon'
import { Input } from '../input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'
import { Skeleton } from '../skeleton'
import { twMerge } from 'tailwind-merge'
import { Move } from 'lucide-react'

// Draggable Row Component
interface DraggableRowProps<TData, TValue> {
  row: Row<TData>
  isExpandable: boolean
  isDraggable: boolean
  isExpanded: boolean
  expandedIds: string[]
  setExpandedIds: (ids: string[]) => void
  renderExpandedContent?: (row: Row<TData>) => React.ReactNode
  columns: ColumnDef<TData, TValue>[]
  onRowReorder?: (sourceId: string, targetId: string) => void
  getRowId: (row: TData, index: number) => string
}

function DraggableRow<TData, TValue>({
  row,
  isExpandable,
  isDraggable,
  isExpanded,
  expandedIds,
  setExpandedIds,
  renderExpandedContent,
  columns,
  onRowReorder,
  getRowId,
}: DraggableRowProps<TData, TValue>) {
  const rowId = getRowId(row.original, row.index)

  const [{ isDragging }, drag] = useDrag({
    type: 'row',
    item: { id: rowId, index: row.index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const [{ isOver }, drop] = useDrop({
    accept: 'row',
    drop: (item: { id: string; index: number }) => {
      if (item.id !== rowId && onRowReorder) {
        onRowReorder(item.id, rowId)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  const toggleRowExpansion = () => {
    const isExpanded = expandedIds.includes(rowId)
    if (isExpanded) {
      setExpandedIds(expandedIds.filter((id) => id !== rowId))
    } else {
      setExpandedIds([...expandedIds, rowId])
    }
  }

  return (
    <React.Fragment>
      <TableRow
        ref={(node) => {
          drag(drop(node))
        }}
        className={`border-b border-neutral-main/10 ${
          isDragging ? 'opacity-50' : ''
        } ${isOver ? 'bg-primary-main/10' : ''}`}
        data-state={row.getIsSelected() && 'selected'}
      >
        {isExpandable && (
          <TableCell className='w-12 flex justify-center items-center'>
            <Button
              variant='ghost'
              size='sm'
              className='h-6 w-6 p-0'
              onClick={toggleRowExpansion}
            >
              <Icon
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                className='h-4 w-4 transition-transform'
              />
            </Button>
          </TableCell>
        )}
        {isDraggable && (
          <TableCell className='w-12 flex justify-center items-center'>
            <div className='cursor-move'>
              <Move className='h-4 w-4 text-neutral-main/60' />
            </div>
          </TableCell>
        )}
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
      {isExpandable && isExpanded && renderExpandedContent && (
        <TableRow>
          <TableCell
            colSpan={
              columns.length + (isExpandable ? 1 : 0) + (isDraggable ? 1 : 0)
            }
            className='p-0'
          >
            <div className='p-md'>{renderExpandedContent(row)}</div>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  )
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  searchKey?: string
  isLoading?: boolean
  total?: number
  pageSize?: number
  onPageChange?: (page: number) => void
  currentPage?: number
  children?: React.ReactNode
  // Expandable rows props
  isExpandable?: boolean
  renderExpandedContent?: (row: Row<TData>) => React.ReactNode
  expandedRowIds?: string[]
  onExpandedRowIdsChange?: (expandedIds: string[]) => void
  noFilters?: boolean
  // Infinite scroll props
  enableInfiniteScroll?: boolean
  onLoadMore?: () => void
  hasMore?: boolean
  loadingMore?: boolean
  // Internal infinite scroll management
  allData?: TData[]
  setAllData?: (data: TData[] | ((prev: TData[]) => TData[])) => void
  setCurrentPage?: (page: number | ((prev: number) => number)) => void
  // Drag and drop props
  isDraggable?: boolean
  onRowReorder?: (sourceId: string, targetId: string) => void
  getRowId?: (row: TData) => string
}

export function AdminTable<TData, TValue>({
  columns,
  data,
  searchKey,
  isLoading = false,
  total = 0,
  pageSize = 10,
  onPageChange,
  currentPage = 1,
  children,
  isExpandable = false,
  renderExpandedContent,
  expandedRowIds = [],
  onExpandedRowIdsChange,
  noFilters = false,
  enableInfiniteScroll = false,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  allData,
  setAllData,
  setCurrentPage,
  isDraggable = false,
  onRowReorder,
  getRowId,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [showFilters, setShowFilters] = React.useState(false)
  const [internalExpandedRowIds, setInternalExpandedRowIds] = React.useState<
    string[]
  >([])

  // Infinite scroll ref
  const loadMoreRef = React.useRef<HTMLDivElement>(null)

  // Default getRowId function
  const defaultGetRowId = React.useCallback(
    (row: TData, index: number) => {
      if (getRowId) {
        return getRowId(row)
      }
      return index.toString()
    },
    [getRowId],
  )

  // Internal infinite scroll logic
  React.useEffect(() => {
    if (enableInfiniteScroll && setAllData && data.length > 0) {
      if (currentPage === 1) {
        // Reset data when filters change
        setAllData(data)
      } else {
        // Append new data to existing data
        setAllData((prev: TData[]) => [...prev, ...data])
      }
    }
  }, [enableInfiniteScroll, setAllData, data, currentPage])

  // Internal load more handler
  const handleInternalLoadMore = React.useCallback(() => {
    if (enableInfiniteScroll && setCurrentPage) {
      setCurrentPage((prev) => prev + 1)
    }
  }, [enableInfiniteScroll, setCurrentPage])

  // Use internal or external load more handler
  const finalOnLoadMore = onLoadMore || handleInternalLoadMore

  // Use controlled or uncontrolled expanded row IDs
  const expandedIds =
    expandedRowIds.length > 0 ? expandedRowIds : internalExpandedRowIds
  const setExpandedIds = onExpandedRowIdsChange || setInternalExpandedRowIds

  // Intersection Observer for infinite scroll
  React.useEffect(() => {
    if (!enableInfiniteScroll || !finalOnLoadMore || !hasMore || loadingMore) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting && hasMore && !loadingMore) {
          finalOnLoadMore()
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.1,
      },
    )

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [enableInfiniteScroll, finalOnLoadMore, hasMore, loadingMore, currentPage])

  // Use allData if available, otherwise use data
  const tableData = enableInfiniteScroll && allData ? allData : data

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      expanded: expandedIds.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
    },
    pageCount: Math.ceil(total / pageSize),
    manualPagination: true,
    getRowCanExpand: () => isExpandable,
    onExpandedChange: (updater) => {
      const newExpanded = typeof updater === 'function' ? updater({}) : updater
      const expandedRowIds = Object.keys(newExpanded).filter(
        (key) => newExpanded[key as keyof typeof newExpanded],
      )
      setExpandedIds(expandedRowIds)
    },
  })

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page)
    }
  }

  const totalPages = Math.ceil(total / pageSize)

  const tableContent = (
    <div>
      <div className='flex items-center py-4 gap-md'>
        {searchKey ? (
          <Input
            placeholder='Search...'
            value={
              (table.getColumn(searchKey)?.getFilterValue() as string) ?? ''
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              table.getColumn(searchKey)?.setFilterValue(event.target.value)
            }
            className='w-full'
          />
        ) : (
          <div className='flex-1' />
        )}
        {children && (
          <Button
            variant='ghost'
            className='px-md min-w-40'
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            <Icon
              name={showFilters ? 'ChevronUp' : 'ChevronDown'}
              className='ml-2'
            />
          </Button>
        )}
        {!noFilters && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className='px-md'>
                Columns <Icon name='ChevronDown' className='ml-2' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className='capitalize'
                      checked={column.getIsVisible()}
                      onCheckedChange={(value: boolean) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {showFilters && <div className='mb-sm'>{children}</div>}
      <div className='rounded-md border border-neutral-main/10 overflow-hidden'>
        <Table>
          <TableHeader className='bg-neutral-main/5 rounded-md'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {isExpandable && <TableHead className='w-12'></TableHead>}
                {isDraggable && <TableHead className='w-12'></TableHead>}
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {isExpandable && (
                    <TableCell className='w-12'>
                      <Skeleton className='h-4 w-4' />
                    </TableCell>
                  )}
                  {isDraggable && (
                    <TableCell className='w-12'>
                      <Skeleton className='h-4 w-4' />
                    </TableCell>
                  )}
                  {columns.map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton className='h-10 w-full' />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              <>
                {table.getRowModel().rows.map((row) => {
                  const isExpanded = expandedIds.includes(row.id)

                  if (isDraggable) {
                    return (
                      <DraggableRow
                        key={row.id}
                        row={row}
                        isExpandable={isExpandable}
                        isDraggable={isDraggable}
                        isExpanded={isExpanded}
                        expandedIds={expandedIds}
                        setExpandedIds={setExpandedIds}
                        renderExpandedContent={renderExpandedContent}
                        columns={columns}
                        onRowReorder={onRowReorder}
                        getRowId={defaultGetRowId}
                      />
                    )
                  }

                  // Regular row rendering when drag and drop is disabled
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        className='border-b border-neutral-main/10'
                        data-state={row.getIsSelected() && 'selected'}
                      >
                        {isExpandable && (
                          <TableCell className='w-12 flex justify-center items-center'>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='h-6 w-6 p-0'
                              onClick={() => {
                                const isExpanded = expandedIds.includes(row.id)
                                if (isExpanded) {
                                  setExpandedIds(
                                    expandedIds.filter((id) => id !== row.id),
                                  )
                                } else {
                                  setExpandedIds([...expandedIds, row.id])
                                }
                              }}
                            >
                              <Icon
                                name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                                className='h-4 w-4 transition-transform'
                              />
                            </Button>
                          </TableCell>
                        )}
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      {isExpandable && isExpanded && renderExpandedContent && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + (isExpandable ? 1 : 0)}
                            className='p-0'
                          >
                            <div className='p-md'>
                              {renderExpandedContent(row)}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
                {/* Infinite scroll loading indicator */}
                {enableInfiniteScroll && loadingMore && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        columns.length +
                        (isExpandable ? 1 : 0) +
                        (isDraggable ? 1 : 0)
                      }
                      className='h-16 text-center'
                    >
                      <div className='flex items-center justify-center gap-2'>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary-main'></div>
                        <span className='text-sm text-neutral-main/60'>
                          Loading more...
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {/* Infinite scroll trigger element */}
                {enableInfiniteScroll && hasMore && !loadingMore && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        columns.length +
                        (isExpandable ? 1 : 0) +
                        (isDraggable ? 1 : 0)
                      }
                      className='h-20 p-0'
                    >
                      <div
                        ref={loadMoreRef}
                        className='h-20 flex flex-col items-center justify-center bg-neutral-main/5 gap-2'
                      >
                        <div className='text-sm text-neutral-main/60'>
                          Scroll to load more... ({tableData.length} items
                          loaded)
                        </div>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => finalOnLoadMore()}
                          disabled={loadingMore}
                        >
                          {loadingMore ? 'Loading...' : 'Load More'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {/* End of data indicator */}
                {enableInfiniteScroll &&
                  !hasMore &&
                  !loadingMore &&
                  tableData.length > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={
                          columns.length +
                          (isExpandable ? 1 : 0) +
                          (isDraggable ? 1 : 0)
                        }
                        className='h-16 text-center'
                      >
                        <div className='text-sm text-neutral-main/40'>
                          All data loaded - Showing {tableData.length} results
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
              </>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (isExpandable ? 1 : 0) +
                    (isDraggable ? 1 : 0)
                  }
                  className='h-24 text-center'
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Show pagination only when infinite scroll is disabled */}
      {!enableInfiniteScroll && total > 0 && (
        <div className='flex items-center justify-between py-4'>
          <div className='text-sm text-muted-foreground'>
            Showing {data.length} of {total} results
          </div>
          <div className='flex items-center space-x-2'>
            <Button
              variant='transparent'
              className='!bg-transparent'
              size='sm'
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1 || isLoading}
            >
              <Icon
                name='ChevronRight'
                className={twMerge(currentPage <= 1 && 'opacity-50')}
              />
            </Button>
            <div className='flex items-center gap-1'>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'ghost' : 'transparent'}
                    size='sm'
                    className='h-8 w-8 p-0 rounded-full'
                    onClick={() => handlePageChange(page)}
                    disabled={isLoading}
                  >
                    {page}
                  </Button>
                ),
              )}
            </div>
            <Button
              variant='transparent'
              className='!bg-transparent'
              size='sm'
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || isLoading}
            >
              <Icon
                name='ChevronLeft'
                className={twMerge(
                  (currentPage >= totalPages || isLoading) && 'opacity-50',
                )}
              />
            </Button>
          </div>
        </div>
      )}
    </div>
  )

  if (isDraggable) {
    return <DndProvider backend={HTML5Backend}>{tableContent}</DndProvider>
  }

  return tableContent
}
